import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiCard from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { TotpMultiFactorGenerator } from 'firebase/auth';
import SitemarkIcon from '../shared/components/SitemarkIcon';
import { PENDING_TRUST_DEVICE_KEY } from './trustedDeviceUtils';

const AUTH_ERROR_INVALID_CODE = 'auth/invalid-verification-code';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

/**
 * @param {{
 *   resolver: import('firebase/auth').MultiFactorResolver,
 *   onBack: () => void,
 * }} props
 */
export default function MfaChallengeCard({ resolver, onBack }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shouldTrustDevice, setShouldTrustDevice] = useState(false);

  const handleSubmit = async () => {
    if (code.length !== 6 || isLoading || !resolver) return;
    setIsLoading(true);
    setError('');
    try {
      const hint = resolver.hints.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID);
      if (!hint) {
        setError('No authenticator found. Please contact support.');
        return;
      }
      // Write trust intent synchronously before resolveSignIn triggers onAuthStateChanged.
      // SignInCard reads this flag and calls trustDevice before navigating.
      if (shouldTrustDevice) {
        try { sessionStorage.setItem(PENDING_TRUST_DEVICE_KEY, '1'); } catch {}
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
      await resolver.resolveSignIn(assertion);
      // onAuthStateChanged in SignInCard fires, calls trustDevice, then navigates
    } catch (err) {
      try { sessionStorage.removeItem(PENDING_TRUST_DEVICE_KEY); } catch {}
      const errCode = /** @type {{ code?: string }} */ (err)?.code;
      if (errCode === AUTH_ERROR_INVALID_CODE) {
        setError('Invalid code. Please check your authenticator app and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card variant="outlined">
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <SitemarkIcon sx={{ height: { xs: 28, sm: 36, md: 40 }, width: { xs: 120, sm: 160, md: 200 }, mr: 2, color: '#000000' }} />
      </Box>
      <Stack spacing={2}>
        <Typography component="h1" variant="h5" sx={{ fontSize: 'clamp(1.5rem, 8vw, 1.75rem)' }}>
          Multi&#8209;Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter the 6&#8209;digit code from your authenticator app.
        </Typography>
        <TextField
          label="6-digit code"
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          error={!!error}
          helperText={error}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          fullWidth
          autoFocus
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={shouldTrustDevice}
              onChange={(e) => setShouldTrustDevice(e.target.checked)}
            />
          }
          label="Trust this device"
        />
        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={code.length !== 6 || isLoading}
        >
          {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Verify Code'}
        </Button>
        <Button variant="text" onClick={onBack}>
          Back to sign in
        </Button>
      </Stack>
    </Card>
  );
}
